import { Box, SxProps, Theme, Typography } from '@mui/material';
import React from 'react';

import { LiveSearchResponse } from '../../../../../@types';
import { displayNodeTypeName } from '../../../../../utils/node-type';

/**
 * A search result row for the share picker.
 *
 * The shared `LocationSearchItem` molecule is deliberately NOT used here: it
 * expects the job/location search shape (`clientOrContractData`,
 * `addressOrParentData`, `referenceNo1`, `nodeTypeName`), none of which exist on
 * `LiveSearchResponse`. Feeding it this shape rendered an empty 40px logo slot
 * and three blank caption lines — a ~90px tall, mostly empty row. That molecule
 * has 57 consumers, so the fix belongs at this call site.
 */

/** ~40px tall, matching the 12px/py:0.5 density of the table below the picker. */
const OPTION_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  py: 0.75,
  borderBottom: '1px solid',
  borderColor: 'v4.border',
  '&:last-of-type': { borderBottom: 'none' },
  '&.MuiAutocomplete-option': { minHeight: 'auto' },
  '&.MuiAutocomplete-option.Mui-focused': { bgcolor: 'v4.tableHover' },
};

const NAME_SX: SxProps<Theme> = { fontSize: '13px', fontWeight: 500, lineHeight: 1.4 };

const CODE_SX: SxProps<Theme> = {
  fontSize: '11px',
  lineHeight: 1.4,
  color: 'v4.textMuted',
  display: 'block',
};

/**
 * The one accent in the row. "Add people or locations" can return either, and
 * which one it is, is the only thing a user needs to disambiguate two similar
 * names — so the node type carries the teal, not the name.
 */
const TYPE_SX: SxProps<Theme> = {
  flexShrink: 0,
  fontSize: '10px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'v4.accentDark',
};

export interface ShareAccessSearchItemProps {
  /** The `<li>` props MUI Autocomplete supplies for the option (class names, handlers). */
  liProps: React.HTMLAttributes<HTMLLIElement>;
  data: LiveSearchResponse;
}

export const ShareAccessSearchItem: React.FC<ShareAccessSearchItemProps> = ({ liProps, data }) => {
  const typeLabel = displayNodeTypeName(data.nodeType?.name) || data.nodeTypeCode || '';

  return (
    <Box component="li" {...liProps} sx={OPTION_SX}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {/* `title` so a name too long for the popover width is still readable. */}
        <Typography sx={NAME_SX} noWrap title={data.name}>
          {data.name}
        </Typography>
        {data.code && (
          <Typography sx={CODE_SX} noWrap>
            {data.code}
          </Typography>
        )}
      </Box>
      {typeLabel && <Typography sx={TYPE_SX}>{typeLabel}</Typography>}
    </Box>
  );
};
